import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Button, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid-premium';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import { Form, SimpleTextField, useModel, useSettings, useUserData, Utils } from 'app/main/components';
import { DetailsSectionTable, DetailsSectionTableInputCells, ViewTableUtils } from 'app/main/components/page-layouts';

interface OrdersStandardNotesEditDialogProps {
	/** The base model */
	baseModel: any;
	/** If true the dialog is in add mode and not into edit */
	isAdd?: boolean;
	/** Function used to refresh the related view */
	updateRefresh?: VoidFunction;
}

export function showOrdersStandardNotesEditDialog(props: OrdersStandardNotesEditDialogProps) {
	return openDialog({
		children: <OrdersStandardNotesEditDialog {...props} />,
		fullWidth: true,
		maxWidth: 'md',
		onClose: () => {}
	});
}

export default function OrdersStandardNotesEditDialog(props: OrdersStandardNotesEditDialogProps) {
	const dispatch = useDispatch();
	const { settings, t } = useSettings('orders_standardnotes');
	const userData = useUserData();

	const baseModel = { osnt_uid: uuidv4(), osnt_user_created_uid: userData.user_uid, ...props.baseModel };
	const { model, sendChange, save } = useModel({ baseModel, settings, isEdit: true, isAdd: props.isAdd });

	const executeSave = () => {
		save({
			onSuccess: () => {
				dispatch(Utils.alertLocalizedMsg(t, 'ALERT_UPDATE_SUCCESS', 'success'));
				dispatch(closeDialog());
				props.updateRefresh?.();
			}
		});
	};

	const columns: GridColDef[] = [
		{
			field: 'osnr_description',
			headerName: t('ORDERS_STANDARDNOTES_ROWS_COLUMN_DESCRIPTION'),
			minWidth: 200,
			flex: 1,
			renderCell: ViewTableUtils.renderCellWithTooltip,
			editable: true,
			renderEditCell: params => <DetailsSectionTableInputCells.TextEditInputCell {...params} />
		},
		{
			field: 'osnr_order',
			type: 'number',
			headerName: t('ORDERS_STANDARDNOTES_ROWS_COLUMN_ORDER'),
			width: 150,
			editable: true,
			renderEditCell: params => <DetailsSectionTableInputCells.NumberEditInputCell {...params} />
		}
	];

	return (
		<>
			<DialogTitle>
				<FormattedMessage
					id="ordersStandardNotesEditDialog.title"
					defaultMessage={t(props.isAdd ? 'ORDERS_STANDARDNOTES_ADD' : 'DIALOG_TITLE_UPDATE')}
					values={{ module: `"${baseModel.osnt_title}"` }}
				/>
			</DialogTitle>
			<DialogContent>
				<Form id="ordersStandardNotesEditDialog" onSubmit={executeSave}>
					<Grid container spacing={2}>
						{/* TITLE */}
						<Grid size={12}>
							<SimpleTextField
								name="osnt_title"
								label={t('DETAILS_ORDERS_STANDARDNOTES_FIELD_TITLE')}
								value={model.osnt_title}
								isEdit
								sendChange={sendChange}
								required
								validationError={t('ERROR_TEXT_REQUIRED')}
							/>
						</Grid>
						{/* ROWS */}
						<Grid size={12}>
							<DetailsSectionTable
								name="osnt_rows"
								columns={columns}
								idField="osnr_uid"
								rows={model.osnt_rows}
								isEdit
								sendChange={sendChange}
							/>
						</Grid>
					</Grid>
				</Form>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => dispatch(closeDialog())}>{t('DIALOG_ACTION_CANCEL')}</Button>
				<Button type="submit" form="ordersStandardNotesEditDialog" color="secondary">
					{t('DIALOG_ACTION_CONFIRM')}
				</Button>
			</DialogActions>
		</>
	);
}
